import {type ChangeEvent, useState} from "react";
import {toast} from "react-toastify";

interface BulkAddToCartProps {
    templateCsvUrl?: string;
    showUploadButton?: boolean;
}

const CSV_INPUT_ID = 'weka-bulk-cart-csv-upload';

const BulkAddToCart: React.FC<BulkAddToCartProps> = ({
    templateCsvUrl,
    showUploadButton = true,
}) => {
    const [isUploading, setIsUploading] = useState(false);
    const hasTemplateDownload = String(templateCsvUrl || '').trim() !== '';

    const uploadCSVToCart = async (file: File) => {
        if (!file.name.toLowerCase().endsWith('.csv')) {
            toast.error('Please upload a valid CSV file');
            return;
        }

        setIsUploading(true);
        const formData = new FormData();
        formData.append('csv_file', file);

        try {
            const res = await fetch('/wp-json/weka/v1/cart/upload-cart-csv', {
                method: 'POST',
                body: formData,
                credentials: 'include',
            });

            const json = await res.json();

            if (json.success) {
                const addedCount = json.added_count || 0;
                const notFoundCount = json.not_found ? json.not_found.length : 0;

                let message = `Successfully added ${addedCount} product${addedCount !== 1 ? 's' : ''} to cart!`;

                if (notFoundCount > 0) {
                    const skuList = json.not_found.slice(0, 3).join(', ');
                    const moreText = notFoundCount > 3 ? ` and ${notFoundCount - 3} more` : '';
                    message += ` ${notFoundCount} SKU${notFoundCount !== 1 ? 's' : ''} not found: ${skuList}${moreText}`;
                }

                toast.success(message);

                if (typeof window.jQuery !== 'undefined') {
                    window.jQuery(document.body).trigger('wc_fragment_refresh');
                }

                setTimeout(() => {
                    window.location.reload();
                }, 1000);
            } else {
                toast.error(json.message || 'Failed to process CSV file');
            }
        } catch (err) {
            console.error('CSV upload failed:', err);
            toast.error('Failed to upload CSV file. Please try again.');
        } finally {
            setIsUploading(false);
        }
    };

    const onUploadCSV = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            uploadCSVToCart(event.target.files[0]);
            event.target.value = '';
        }
    };

    if (!showUploadButton && !hasTemplateDownload) {
        return null;
    }

    return (
        <div className="bulk-add-to-cart-container">
            {showUploadButton && (
                <button
                    type="button"
                    className={'button button-primary button-small'}
                    onClick={() => document.getElementById(CSV_INPUT_ID)?.click()}
                    disabled={isUploading}
                >
                    {isUploading ? 'UPLOADING...' : 'UPLOAD CSV'}
                </button>
            )}

            {hasTemplateDownload && (
                <a
                    href={templateCsvUrl}
                    className="button button-gray button-small"
                    style={showUploadButton ? {marginLeft: '10px'} : undefined}
                    download="template.csv"
                >
                    DOWNLOAD TEMPLATE
                </a>
            )}

            {showUploadButton && (
                <form method="post" id="UploadCSVFile" action="" encType="multipart/form-data">
                    <input type="hidden" name="uploadcsvfilehidden" value="1"/>
                    <input
                        type="file"
                        name="csv_file"
                        id={CSV_INPUT_ID}
                        accept=".csv"
                        onChange={onUploadCSV}
                        disabled={isUploading}
                    />
                </form>
            )}
        </div>
    );
};

export default BulkAddToCart
